import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import HeaderOne from "../../header/HeaderOne";
import FooterOne from "../../footer/FooterOne";
import Head from "../../components/Head";
import { track } from "../../AnalyticsEvents/track";
import { start_replay } from "../../AnalyticsEvents/posthog";

// Plan feature differentiation. Currently DISPLAY-ONLY — every account
// gets every capability regardless of which plan is shown here. We're
// testing whether the staged feature story moves conversion before
// committing to backend enforcement (subscription flag + middleware).
const plans = [
  {
    name: "Starter",
    price: 25,
    yearlyPrice: 21,
    sim: "3 SIM cards",
    features: [
      { text: "3 SIM cards", included: true },
      { text: "Unlimited messages", included: true },
      { text: "Unlimited GoHighLevel sub-accounts", included: true },
      { text: "REST API + webhooks", included: true },
      { text: "Team logins (subusers)", included: false },
      { text: "Priority support", included: false },
    ],
    cta: "Get Started",
    highlight: false,
  },
  {
    name: "Agency",
    price: 65,
    yearlyPrice: 54,
    sim: "10 SIM cards",
    features: [
      { text: "10 SIM cards", included: true },
      { text: "Unlimited messages", included: true },
      { text: "Unlimited GoHighLevel sub-accounts", included: true },
      { text: "REST API + webhooks", included: true },
      { text: "Team logins (subusers)", included: true },
      { text: "Priority support", included: false },
    ],
    cta: "Get Started",
    highlight: true,
  },
  {
    name: "Scale",
    price: 195,
    yearlyPrice: 162,
    sim: "Unlimited SIM cards",
    features: [
      { text: "Unlimited SIM cards", included: true },
      { text: "Unlimited messages", included: true },
      { text: "Unlimited GoHighLevel sub-accounts", included: true },
      { text: "Dedicated server for your devices", included: true },
      { text: "REST API + webhooks", included: true },
      { text: "Team logins (subusers)", included: true },
      { text: "Priority support (Slack) + custom SLA", included: true },
    ],
    cta: "Get Started",
    highlight: false,
  },
];

function UseYourOwnNumbersPricing() {
  const [isYearly, setIsYearly] = useState(true);
  const navigate = useNavigate();

  // Track + replay pricing page views. This is a high-value moment —
  // users on pricing are evaluating the upgrade decision; replay tells
  // us where they hesitate (price, FAQ, plan-comparison toggle).
  useEffect(() => {
    track('pricing_viewed');
    start_replay('pricing');
  }, []);

  return (
    <>
      <Head
        title="SMS Gateway Pricing — From $25/mo"
        description="Simple, transparent pricing for TextLink's SMS gateway. From $25/mo for 3 SIMs, $65/mo for 10 SIMs, $195/mo for unlimited. 300 free messages on signup, no credit card."
        canonical="/pricing"
      />
      <HeaderOne />

      <main className="pp">
        <div className="pp-container">

          {/* ---- HERO ---- */}
          <header className="pp-hero">
            <span className="pp-label">PRICING</span>
            <h1>Simple, transparent pricing</h1>
            <p>Pick a plan and scale as you grow. No contracts, cancel anytime.</p>
          </header>

          {/* ---- TOGGLE ---- */}
          <div className="pp-toggle">
            <span className={`pp-toggle-text ${!isYearly ? 'pp-toggle-active' : ''}`}>Monthly</span>
            <label className="pp-switch">
              <input type="checkbox" checked={isYearly} onChange={() => setIsYearly(!isYearly)} />
              <span className="pp-slider"></span>
            </label>
            <span className={`pp-toggle-text ${isYearly ? 'pp-toggle-active' : ''}`}>
              Yearly <span className="pp-save-badge">Save 17%</span>
            </span>
          </div>

          {/* ---- CARDS ---- */}
          <div className="pp-grid">
            {plans.map((plan, i) => (
              <div key={i} className={`pp-card${plan.highlight ? ' pp-card--highlight' : ''}`}>
                {plan.highlight && <div className="pp-badge">Most Popular</div>}
                <div className="pp-card-header">
                  <h3>{plan.name}</h3>
                  <div className="pp-price">
                    <span className="pp-currency">$</span>
                    <span className="pp-amount">{isYearly ? plan.yearlyPrice : plan.price}</span>
                    <span className="pp-period">/mo</span>
                  </div>
                  {plan.price > 0 && (
                    <p className="pp-yearly-note">
                      ${isYearly ? Math.round(plan.yearlyPrice * 12) : Math.round(plan.price * 12)}/year
                    </p>
                  )}
                </div>
                <ul className="pp-features">
                  {plan.features.map((f, j) => (
                    <li key={j} className={f.included ? 'pp-feat' : 'pp-feat pp-feat--no'}>
                      <span className="pp-feat-icon">
                        {f.included ? (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                        ) : (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        )}
                      </span>
                      {f.text}
                    </li>
                  ))}
                </ul>
                <button
                  className={plan.highlight ? 'pp-btn pp-btn--fill' : 'pp-btn'}
                  onClick={() => navigate('/signup')}
                >
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>

          {/* ---- INCLUDED ----
              Only items that are truly universal across all plans live
              here. REST API + SDKs and Sub-accounts moved out — they're
              Agency-tier-and-up per the staged feature story. GHL
              integration stays (every plan can connect to GHL; only the
              sub-account count is plan-gated). */}
          <section className="pp-included">
            <span className="pp-label">EVERY PLAN INCLUDES</span>
            <div className="pp-included-grid">
              {[
                { icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>, text: "2-way SMS" },
                { icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>, text: "GHL integration" },
                { icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>, text: "Web dashboard" },
                { icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>, text: "Bulk sender" },
                { icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>, text: "Carrier-grade delivery" },
                { icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>, text: "Compliance tools" },
              ].map((item, i) => (
                <div key={i} className="pp-included-item">
                  <span className="pp-included-icon">{item.icon}</span>
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
          </section>

          {/* ---- CTA ---- */}
          <section className="pp-cta">
            <h2>Have questions about pricing?</h2>
            <p>Check our FAQ for answers about SIM limits, carrier costs, and what's included in each plan.</p>
            <div className="pp-cta-buttons">
              <Link className="pp-btn-primary" to="/faq">
                View FAQ <span className="pp-arrow">→</span>
              </Link>
              <Link className="pp-btn-secondary" to="/Contact">
                Contact Us
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" strokeWidth="2" />
                </svg>
              </Link>
            </div>
          </section>

        </div>
      </main>

      <FooterOne />

      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Geist:wght@400;500;600;700&display=swap');
        .pp {
          padding: clamp(7rem, 16vh, 13rem) 0 clamp(4rem, 8vh, 6rem);
          background: #ffffff;
          font-family: 'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          color: #0a0a0a;
          -webkit-font-smoothing: antialiased;
          letter-spacing: -0.005em;
        }
        .pp-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 clamp(1.25rem, 3vw, 2rem);
          box-sizing: border-box;
        }
        .pp-label {
          font-size: 0.6875rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          color: rgba(0,0,0,0.5);
          display: block;
          margin-bottom: 1.25rem;
          text-align: center;
        }

        /* hero */
        .pp-hero {
          text-align: center;
          margin-bottom: clamp(3rem, 6vh, 4rem);
        }
        .pp-hero h1 {
          font-size: clamp(2.25rem, 5.5vw, 3.75rem);
          font-weight: 600;
          color: #0a0a0a;
          letter-spacing: -0.045em;
          line-height: 1.02;
          margin: 0 0 1.25rem;
          max-width: 22ch;
          margin-left: auto;
          margin-right: auto;
        }
        .pp-hero p {
          font-size: clamp(0.9375rem, 1.5vw, 1.0625rem);
          color: rgba(0,0,0,0.58);
          max-width: 520px;
          margin: 0 auto;
          line-height: 1.55;
        }

        /* toggle */
        .pp-toggle {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          margin-bottom: clamp(2.5rem, 5vh, 3.5rem);
        }
        .pp-toggle-text {
          font-size: 0.9375rem;
          font-weight: 500;
          color: #a1a1aa;
          transition: color 0.15s ease;
        }
        .pp-toggle-active {
          color: #0a0a0a;
        }
        .pp-save-badge {
          display: inline-block;
          background: #0a0a0a;
          color: #fff;
          font-size: 0.625rem;
          font-weight: 600;
          padding: 0.15rem 0.5rem;
          border-radius: 10px;
          margin-left: 0.375rem;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          vertical-align: middle;
        }
        .pp-switch {
          position: relative;
          display: inline-block;
          width: 48px;
          height: 26px;
        }
        .pp-switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        .pp-slider {
          position: absolute;
          cursor: pointer;
          inset: 0;
          background: #e4e4e7;
          border-radius: 26px;
          transition: background 0.2s ease;
        }
        .pp-slider:before {
          content: '';
          position: absolute;
          height: 20px;
          width: 20px;
          left: 3px;
          bottom: 3px;
          background: #fff;
          border-radius: 50%;
          transition: transform 0.2s ease;
          box-shadow: 0 1px 3px rgba(0,0,0,0.12);
        }
        .pp-switch input:checked + .pp-slider {
          background: #0a0a0a;
        }
        .pp-switch input:checked + .pp-slider:before {
          transform: translateX(22px);
        }

        /* grid */
        .pp-grid {
          display: grid;
          gap: 1rem;
          grid-template-columns: repeat(3, 1fr);
          max-width: 900px;
          margin-left: auto;
          margin-right: auto;
          margin-bottom: clamp(3rem, 6vh, 4rem);
        }
        @media (max-width: 900px) {
          .pp-grid { grid-template-columns: repeat(2, 1fr); max-width: 620px; }
        }
        @media (max-width: 600px) {
          .pp-grid { grid-template-columns: 1fr; max-width: 380px; }
        }

        /* card */
        .pp-card {
          position: relative;
          background: #fff;
          border: 1px solid #e4e4e7;
          border-radius: 14px;
          padding: clamp(1.5rem, 3vw, 2rem);
          display: flex;
          flex-direction: column;
          transition: all 0.15s ease;
        }
        .pp-card:hover {
          border-color: #d4d4d8;
          box-shadow: 0 4px 16px rgba(0,0,0,0.06);
        }
        .pp-card--highlight {
          border-color: rgba(124, 58, 237, 0.4);
          background: linear-gradient(180deg, #faf7ff 0%, #ffffff 60%);
          box-shadow: 0 18px 40px -14px rgba(124, 58, 237, 0.2);
        }
        .pp-badge {
          position: absolute;
          top: -12px;
          left: 50%;
          transform: translateX(-50%);
          background: linear-gradient(135deg, #7c3aed 0%, #a855f7 100%);
          color: #fff;
          font-size: 0.6875rem;
          font-weight: 600;
          padding: 0.28rem 0.875rem;
          border-radius: 20px;
          white-space: nowrap;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          box-shadow: 0 6px 16px -4px rgba(124, 58, 237, 0.4);
        }

        /* card header */
        .pp-card-header {
          margin-bottom: 1.5rem;
          padding-bottom: 1.5rem;
          border-bottom: 1px solid #f4f4f5;
        }
        .pp-card-header h3 {
          font-size: 0.9375rem;
          font-weight: 600;
          color: #0a0a0a;
          margin: 0 0 0.75rem;
          letter-spacing: -0.01em;
        }
        .pp-price {
          display: flex;
          align-items: baseline;
          gap: 0.125rem;
        }
        .pp-currency {
          font-size: 1.25rem;
          font-weight: 600;
          color: #0a0a0a;
        }
        .pp-amount {
          font-size: clamp(2.5rem, 5vw, 3rem);
          font-weight: 700;
          color: #0a0a0a;
          letter-spacing: -0.04em;
          line-height: 1;
        }
        .pp-period {
          font-size: 0.9375rem;
          color: rgba(0,0,0,0.58);
          font-weight: 400;
          margin-left: 0.125rem;
        }
        .pp-yearly-note {
          font-size: 0.8125rem;
          color: #a1a1aa;
          margin: 0.5rem 0 0;
        }

        /* features */
        .pp-features {
          list-style: none;
          padding: 0;
          margin: 0 0 1.5rem;
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 0.625rem;
        }
        .pp-feat {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
          color: #0a0a0a;
        }
        .pp-feat--no {
          color: #a1a1aa;
        }
        .pp-feat-icon {
          display: flex;
          align-items: center;
          flex-shrink: 0;
        }
        .pp-feat .pp-feat-icon { color: #0a0a0a; }
        .pp-feat--no .pp-feat-icon { color: #d4d4d8; }

        /* card button */
        .pp-btn {
          display: block;
          width: 100%;
          text-align: center;
          padding: 0.75rem 1.5rem;
          border-radius: 10px;
          font-weight: 500;
          font-size: 0.9375rem;
          color: #0a0a0a;
          border: 1px solid #e4e4e7;
          background: transparent;
          cursor: pointer;
          transition: all 0.15s ease;
          margin-top: auto;
        }
        .pp-btn:hover {
          background: #fafafa;
          border-color: #d4d4d8;
        }
        .pp-btn--fill {
          background: #0a0a0a;
          color: #fff;
          border-color: #0a0a0a;
        }
        .pp-btn--fill:hover {
          background: #27272a;
          color: #fff;
        }

        /* included */
        .pp-included {
          margin-bottom: clamp(3rem, 6vh, 4rem);
        }
        .pp-included-grid {
          display: flex;
          justify-content: center;
          flex-wrap: wrap;
          gap: 1rem;
          margin-top: 1.25rem;
        }
        .pp-included-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background: #fff;
          border: 1px solid #e4e4e7;
          border-radius: 10px;
          padding: 0.75rem 1.25rem;
          font-size: 0.875rem;
          font-weight: 500;
          color: #0a0a0a;
          transition: all 0.15s ease;
        }
        .pp-included-item:hover {
          border-color: #d4d4d8;
          box-shadow: 0 2px 8px rgba(0,0,0,0.04);
        }
        .pp-included-icon {
          color: rgba(0,0,0,0.58);
          display: flex;
          align-items: center;
        }

        /* cta */
        .pp-cta {
          text-align: center;
          padding: clamp(2.5rem, 5vw, 4rem) clamp(1.5rem, 3vw, 2rem);
          border-radius: 14px;
          background: #fff;
          border: 1px solid #e4e4e7;
        }
        .pp-cta h2 {
          font-size: clamp(1.5rem, 3vw, 1.75rem);
          margin: 0 0 0.75rem;
          font-weight: 600;
          color: #0a0a0a;
          letter-spacing: -0.03em;
        }
        .pp-cta p {
          color: rgba(0,0,0,0.58);
          margin: 0 0 2rem;
          font-size: 1rem;
        }
        .pp-cta-buttons {
          display: flex;
          gap: 0.75rem;
          justify-content: center;
          flex-wrap: wrap;
        }
        .pp-btn-primary {
          background: #0a0a0a;
          color: #fff;
          padding: 0.875rem 2rem;
          border-radius: 10px;
          font-weight: 500;
          font-size: 0.9375rem;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 0.375rem;
          transition: all 0.15s ease;
        }
        .pp-btn-primary:hover {
          background: #27272a;
          color: #fff;
          transform: translateY(-1px);
        }
        .pp-arrow {
          transition: transform 0.15s ease;
        }
        .pp-btn-primary:hover .pp-arrow {
          transform: translateX(2px);
        }
        .pp-btn-secondary {
          padding: 0.875rem 2rem;
          border-radius: 10px;
          font-weight: 500;
          font-size: 0.9375rem;
          text-decoration: none;
          background: transparent;
          color: #52525b;
          border: 1px solid #e4e4e7;
          display: inline-flex;
          align-items: center;
          gap: 0.375rem;
          transition: all 0.15s ease;
        }
        .pp-btn-secondary:hover {
          background: #fff;
          color: #0a0a0a;
        }

        /* mobile */
        @media (max-width: 767px) {
          .pp { padding: 7rem 0 2rem; }
          .pp-hero h1 { font-size: 1.5rem; }
          .pp-included-grid { gap: 0.625rem; }
          .pp-included-item { padding: 0.625rem 1rem; font-size: 0.8125rem; }
          .pp-cta-buttons { flex-direction: column; align-items: center; }
          .pp-btn-primary, .pp-btn-secondary { width: 100%; max-width: 280px; justify-content: center; }
        }
      `}</style>
    </>
  );
}

export default UseYourOwnNumbersPricing;
